import React, { useEffect, useMemo, useState } from "react";
import { Toaster, useId, useToastController, Toast, ToastTitle } from "@fluentui/react-components";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    IconButton,
    Typography,
    Box,
    useMediaQuery
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import i18next from "i18next";
import { deleteProductInstruction, fetchProductInstructions, saveProductInstruction } from "../../api/api";
import { appInfo } from "../../authConfig";
import { resolveProductLocale } from "../chat/utils/sessionHelpers";

interface VersionOption {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
}

interface ProductOption {
    manual_key: string;
    faq_key: string;
    text: string;
    is_sub_only: boolean;
    versions: VersionOption[];
}

interface InstructionEntry {
    id: string;
    created_by: string;
    product_index: string;
    manual_key: string;
    faq_key: string;
    product_name: string;
    manual_version_key: string;
    faq_version_key: string;
    version_label: string;
    instructions_ja: string;
    instructions_en: string;
}

const emptyForm = (): InstructionEntry => ({
    id: "",
    created_by: "",
    product_index: "",
    manual_key: "",
    faq_key: "",
    product_name: "",
    manual_version_key: "",
    faq_version_key: "",
    version_label: "",
    instructions_ja: "",
    instructions_en: ""
});

const Spinner: React.FC = () => (
    <div className="full-page-spinner">
        <div className="spinner"></div>
    </div>
);

const ProductInstructions: React.FC = () => {
    const toasterId = useId("product-instructions-toaster");
    const { dispatchToast } = useToastController(toasterId);
    const isWideScreen = useMediaQuery("(min-width:1400px)");

    const [instructions, setInstructions] = useState<InstructionEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [formValue, setFormValue] = useState<InstructionEntry>(emptyForm());
    const [dialogInitialValue, setDialogInitialValue] = useState<InstructionEntry>(emptyForm());
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

    const locale = resolveProductLocale(sessionStorage.getItem("selectedLocale") ?? "ja", appInfo);
    const productOptions: ProductOption[] = useMemo(() => {
        const products = appInfo?.product_categories?.[locale] ?? [];
        return products
            .filter((item: any) => item?.text && item.text !== "ALL")
            .map((item: any) => ({
                manual_key: item.manual_key ?? "",
                faq_key: item.faq_key ?? "",
                text: item.text,
                is_sub_only: item.is_sub_only === true || item.is_sub_only === "true",
                versions: Array.isArray(item.versions)
                    ? item.versions.map((version: any) => ({
                          manual_version_key: version.manual_version_key ?? "",
                          faq_version_key: version.faq_version_key ?? "",
                          text: version.text ?? ""
                      }))
                    : []
            }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale]);

    const findProductOption = (entry: InstructionEntry): ProductOption | null =>
        productOptions.find(option => option.manual_key === entry.manual_key && option.faq_key === entry.faq_key) ?? null;

    const findVersionOption = (entry: InstructionEntry): VersionOption | null => {
        const product = findProductOption(entry);
        if (!product) return null;
        return (
            product.versions.find(version => version.manual_version_key === entry.manual_version_key && version.faq_version_key === entry.faq_version_key) ??
            null
        );
    };

    const showToast = (message: string, intent: "success" | "error" = "success") => {
        const intentStyles = {
            success: { color: "#28a745" },
            error: { color: "#dc3545" }
        };
        dispatchToast(
            <Toast style={{ backgroundColor: "white", borderRadius: "10px", ...intentStyles[intent] }}>
                <ToastTitle style={{ color: "black", fontWeight: "bold" }}>{message}</ToastTitle>
            </Toast>,
            { intent }
        );
    };

    const normalizeInstructions = (rawInstructions: any[]): InstructionEntry[] =>
        (Array.isArray(rawInstructions) ? rawInstructions : []).map(item => ({
            id: item?.id ?? "",
            created_by: item?.created_by ?? "",
            product_index: item?.product_index ?? "",
            manual_key: item?.manual_key ?? "",
            faq_key: item?.faq_key ?? "",
            product_name: item?.product_name ?? "",
            manual_version_key: item?.manual_version_key ?? "",
            faq_version_key: item?.faq_version_key ?? "",
            version_label: item?.version_label ?? "",
            instructions_ja: item?.instructions_ja ?? "",
            instructions_en: item?.instructions_en ?? ""
        }));

    const loadInstructions = async () => {
        setIsLoading(true);
        try {
            const response = await fetchProductInstructions();
            const result = await response.json();
            if (result?.error) {
                showToast(i18next.t("productInstructions.loadError"), "error");
            } else {
                setInstructions(normalizeInstructions(result?.instructions));
            }
        } catch (error) {
            console.error("Error loading product instructions:", error);
            showToast(i18next.t("productInstructions.loadError"), "error");
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadInstructions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openAddDialog = () => {
        const initialValue = emptyForm();
        setEditIndex(null);
        setFormValue(initialValue);
        setDialogInitialValue(initialValue);
        setDialogOpen(true);
    };

    const openEditDialog = (index: number) => {
        const existing = instructions[index];
        const initialValue = { ...emptyForm(), ...existing };
        setEditIndex(index);
        setFormValue(initialValue);
        setDialogInitialValue(initialValue);
        setDialogOpen(true);
    };

    const closeDialog = (force = false) => {
        const hasUnsavedDialogChanges = JSON.stringify(formValue) !== JSON.stringify(dialogInitialValue);
        if (!force && hasUnsavedDialogChanges) {
            setDiscardDialogOpen(true);
            return;
        }
        setDialogOpen(false);
        setEditIndex(null);
    };

    const handleDialogClose = () => {
        closeDialog();
    };

    const discardDialogChanges = () => {
        setDiscardDialogOpen(false);
        closeDialog(true);
    };

    const handleDialogSave = async () => {
        if (!formValue.product_index || !formValue.product_name) {
            showToast(i18next.t("productInstructions.productRequired"), "error");
            return;
        }
        if (!formValue.instructions_ja.trim() && !formValue.instructions_en.trim()) {
            showToast(i18next.t("productInstructions.instructionsRequired"), "error");
            return;
        }
        setIsSaving(true);
        try {
            const response = await saveProductInstruction(formValue);
            const result = await response.json();
            if (!response.ok || result?.error) {
                showToast(result?.error || i18next.t("productInstructions.saveError"), "error");
                return;
            }
            const savedEntry = normalizeInstructions([result?.instruction])[0];
            if (!savedEntry?.id) {
                showToast(i18next.t("productInstructions.saveError"), "error");
                return;
            }
            setInstructions(prev => {
                if (editIndex === null) return [...prev, savedEntry];
                return prev.map((entry, index) => (index === editIndex ? savedEntry : entry));
            });
            showToast(i18next.t("productInstructions.saveSuccess"));
            closeDialog(true);
        } catch (error) {
            console.error("Error saving product instruction:", error);
            showToast(i18next.t("productInstructions.saveError"), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (deleteIndex === null) return;
        const entry = instructions[deleteIndex];
        if (!entry?.id || !entry.product_index) {
            showToast(i18next.t("productInstructions.deleteError"), "error");
            return;
        }
        setIsSaving(true);
        try {
            const response = await deleteProductInstruction(entry.id, entry.product_index);
            const result = await response.json();
            if (!response.ok || result?.error) {
                showToast(result?.error || i18next.t("productInstructions.deleteError"), "error");
            } else {
                setInstructions(prev => prev.filter((_, index) => index !== deleteIndex));
                setDeleteIndex(null);
                showToast(i18next.t("productInstructions.deleteSuccess"));
            }
        } catch (error) {
            console.error("Error deleting product instruction:", error);
            showToast(i18next.t("productInstructions.deleteError"), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const selectedProductOption = findProductOption(formValue);
    const versionOptionsForSelectedProduct = selectedProductOption?.versions ?? [];
    const selectedVersionOption = findVersionOption(formValue);
    const versionInputValue = selectedProductOption?.is_sub_only ? i18next.t("productInstructions.noVersions") : (selectedVersionOption?.text ?? "");

    return (
        <div>
            {(isLoading || isSaving) && <Spinner />}
            <Toaster toasterId={toasterId} position="bottom-start" pauseOnHover pauseOnWindowBlur timeout={2000} />
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    padding: "13px",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px"
                }}
            >
                <h2
                    style={{
                        fontSize: isWideScreen ? "xxx-large" : "xx-large",
                        fontWeight: "bold",
                        fontFamily: "BIZ UDPGothic, sans-serif",
                        margin: 0
                    }}
                >
                    {i18next.t("productInstructions.pageTitle")}
                </h2>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddDialog}>
                        {i18next.t("productInstructions.addInstruction")}
                    </Button>
                </Box>
            </div>

            <Typography variant="body2" color="text.secondary" sx={{ px: "13px", pb: 2 }}>
                {i18next.t("productInstructions.pageDescription")}
            </Typography>

            <TableContainer component={Paper} sx={{ mx: "13px", width: "calc(100% - 26px)" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{i18next.t("productInstructions.product")}</TableCell>
                            <TableCell>{i18next.t("productInstructions.version")}</TableCell>
                            <TableCell>{i18next.t("productInstructions.instructionsJa")}</TableCell>
                            <TableCell>{i18next.t("productInstructions.instructionsEn")}</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instructions.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {i18next.t("productInstructions.noInstructions")}
                                </TableCell>
                            </TableRow>
                        )}
                        {instructions.map((entry, index) => (
                            <TableRow key={entry.id || `${entry.product_index}-${index}`}>
                                <TableCell>{entry.product_name}</TableCell>
                                <TableCell>{entry.version_label || i18next.t("productInstructions.allVersions")}</TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        title={entry.instructions_ja}
                                        sx={{
                                            maxWidth: 320,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {entry.instructions_ja}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        title={entry.instructions_en}
                                        sx={{
                                            maxWidth: 320,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {entry.instructions_en}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEditDialog(index)} title={i18next.t("productInstructions.edit")}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => setDeleteIndex(index)} title={i18next.t("productInstructions.delete")}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth={false} fullWidth PaperProps={{ sx: { width: "70vw", maxWidth: "70vw" } }}>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: "bold" }}>
                    <span>{i18next.t(editIndex !== null ? "productInstructions.editInstruction" : "productInstructions.addInstruction")}</span>
                    <Button aria-label={i18next.t("productInstructions.closeDialog")} onClick={handleDialogClose} sx={{ minWidth: 0, padding: 0 }}>
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                    <Autocomplete
                        options={productOptions}
                        disabled={editIndex !== null}
                        getOptionLabel={option => option.text}
                        isOptionEqualToValue={(option, value) => option.manual_key === value.manual_key && option.faq_key === value.faq_key}
                        value={selectedProductOption}
                        onChange={(_event, newValue) =>
                            setFormValue(prev => ({
                                ...prev,
                                manual_key: newValue?.manual_key ?? "",
                                faq_key: newValue?.faq_key ?? "",
                                product_index: newValue?.manual_key || newValue?.faq_key || "",
                                product_name: newValue?.text ?? "",
                                manual_version_key: "",
                                faq_version_key: "",
                                version_label: ""
                            }))
                        }
                        renderInput={params => <TextField {...params} label={i18next.t("productInstructions.product")} required size="small" />}
                    />
                    <Autocomplete
                        options={versionOptionsForSelectedProduct}
                        getOptionLabel={option => option.text}
                        isOptionEqualToValue={(option, value) =>
                            option.manual_version_key === value.manual_version_key && option.faq_version_key === value.faq_version_key
                        }
                        value={selectedVersionOption}
                        inputValue={versionInputValue}
                        disabled={!selectedProductOption || selectedProductOption.is_sub_only || versionOptionsForSelectedProduct.length === 0}
                        onChange={(_event, newValue) =>
                            setFormValue(prev => ({
                                ...prev,
                                manual_version_key: newValue?.manual_version_key ?? "",
                                faq_version_key: newValue?.faq_version_key ?? "",
                                version_label: newValue?.text ?? ""
                            }))
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={i18next.t("productInstructions.version")}
                                helperText={i18next.t("productInstructions.versionHelp")}
                                size="small"
                            />
                        )}
                    />
                    <TextField
                        label={i18next.t("productInstructions.instructionsJa")}
                        value={formValue.instructions_ja}
                        onChange={e => setFormValue(prev => ({ ...prev, instructions_ja: e.target.value }))}
                        helperText={i18next.t("productInstructions.instructionsHelp")}
                        fullWidth
                        multiline
                        minRows={4}
                        size="small"
                    />
                    <TextField
                        label={i18next.t("productInstructions.instructionsEn")}
                        value={formValue.instructions_en}
                        onChange={e => setFormValue(prev => ({ ...prev, instructions_en: e.target.value }))}
                        helperText={i18next.t("productInstructions.instructionsHelp")}
                        fullWidth
                        multiline
                        minRows={4}
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} variant="outlined">
                        {i18next.t("productInstructions.cancel")}
                    </Button>
                    <Button onClick={handleDialogSave} variant="contained">
                        {i18next.t("productInstructions.save")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{i18next.t("master.unsavedChanges")}</DialogTitle>
                <DialogContent dividers>{i18next.t("master.unsavedChangesMessage")}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscardDialogOpen(false)} variant="outlined">
                        {i18next.t("productInstructions.cancel")}
                    </Button>
                    <Button onClick={discardDialogChanges} variant="contained" color="error">
                        {i18next.t("productInstructions.discard")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{i18next.t("productInstructions.delete")}</DialogTitle>
                <DialogContent dividers>{i18next.t("productInstructions.deleteConfirm")}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteIndex(null)} variant="outlined">
                        {i18next.t("productInstructions.cancel")}
                    </Button>
                    <Button onClick={confirmDelete} variant="contained" color="error">
                        {i18next.t("productInstructions.delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ProductInstructions;
